// @flow

import React from "react";

import {
    VIDEO_MUTE,
    createToolbarEvent,
    sendAnalytics,
} from "../../../analytics";
import { AbstractDialogTab, hideDialog } from "../../../base/dialog";
import type { Props as AbstractDialogTabProps } from "../../../base/dialog";
import { translate } from "../../../base/i18n";
import JitsiMeetJS, { createLocalTrack } from "../../../base/lib-jitsi-meet";
import { isLocalTrackMuted, getLocalVideoType } from "../../../base/tracks";
import { connect as reactReduxConnect } from "../../../base/redux";
import {
    MEDIA_TYPE,
    setVideoMuted,
    VIDEO_MUTISM_AUTHORITY,
} from "../../../base/media";
import { updateSettings } from "../../../base/settings";
import {
    getDeviceLabelById,
    setVideoInputDevice,
    setAudioInputDeviceAndUpdateSettings,
    setAudioOutputDevice,
} from "../../../base/devices";
import AudioInputPreview from "./AudioInputPreview";
import AudioOutputPreview from "./AudioOutputPreview";
import DeviceSelector from "../../../device-selection/components/DeviceSelector";
import VideoInputPreview from "../../../device-selection/components/VideoInputPreview";
// import cookie from 'react-cookies'
// import {OverflowMenuVideoQualityItem} from '../../video-quality'

// const logger = require("jitsi-meet-logger").getLogger(__filename);

/**
 * The type of the React {@code Component} props of {@link AvDeviceSelection}.
 */
export type Props = {
    ...$Exact<AbstractDialogTabProps>,

    /**
     * All known audio and video devices split by type. This prop comes from
     * the app state.
     */
    availableDevices: Object,

    /**
     * Whether or not the audio selector can be interacted with. If true,
     * the audio input selector will be rendered as disabled. This is
     * specifically used to prevent audio device changing in Firefox, which
     * currently does not work due to a browser-side regression.
     */
    disableAudioInputChange: boolean,

    /**
     * True if device changing is configured to be disallowed. Selectors
     * will display as disabled.
     */
    disableDeviceChange: boolean,

    /**
     * If true, the audio meter will not display. Necessary for browsers or
     * configurations that do not support local stats to prevent a
     * non-responsive mic preview from displaying.
     */
    hideAudioInputPreview: boolean,

    /**
     * Whether or not the audio output source selector should display. If
     * true, the audio output selector and test audio link will not be
     * rendered.
     */
    hideAudioOutputSelect: boolean,

    /**
     * An optional callback to invoke after the component has completed its
     * mount logic.
     */
    mountCallback?: Function,

    /**
     * The id of the audio input device to preview.
     */
    selectedAudioInputId: string,

    /**
     * The id of the audio output device to preview.
     */
    selectedAudioOutputId: string,

    /**
     * The id of the video input device to preview.
     */
    selectedVideoInputId: string,

    /**
     * Invoked to obtain translated strings.
     */
    t: Function,
};

/**
 * The type of the React {@code Component} state of {@link AvDeviceSelection}.
 */
type State = {
    /**
     * Whether or not the audio permission was granted.
     */
    hasAudioPermission: boolean,

    /**
     * Whether or not the audio permission was granted.
     */
    hasVideoPermission: boolean,

    /**
     * The JitsiTrack to use for previewing audio input.
     */
    previewAudioTrack: ?Object,

    /**
     * The JitsiTrack to use for previewing video input.
     */
    previewVideoTrack: ?Object,

    /**
     * The error message from trying to use a video input device.
     */
    previewVideoTrackError: ?string,
};

/**
 * React {@code Component} for previewing audio and video input/output devices.
 *
 * @extends Component
 */
class AvDeviceSelection extends AbstractDialogTab<Props, State> {
    /**
     * Whether current component is mounted or not.
     *
     * In component did mount we start a Promise to create tracks and
     * set the tracks in the state, if we unmount the component in the meanwhile
     * tracks will be created and will never been disposed (dispose tracks is
     * in componentWillUnmount). When tracks are created and component is
     * unmounted we dispose the tracks.
     */
    _unMounted: boolean;

    /**
     * Initializes a new DeviceSelection instance.
     *
     * @param {Object} props - The read-only React Component props with which
     * the new instance is to be initialized.
     */
    constructor(props: Props) {
        super(props);
        const settings = localStorage.getItem("features/base/settings");
        let isCamarActive = true;
        if (settings !== null) {
            if (
                JSON.parse(settings).hasOwnProperty(
                    "userSelectedCameraDeviceId"
                )
            ) {
                isCamarActive =
                    JSON.parse(settings).userSelectedCameraDeviceId !== "N"
                        ? false
                        : true;
            }
        }
        this.state = {
            hasAudioPermission: false,
            hasVideoPermission: false,
            previewAudioTrack: null,
            previewVideoTrack: null,
            previewVideoTrackError: null,
            room: "",
            profile_image: "",
            headline_message: "",
            pin: "",
            switchCamra: isCamarActive,
            cameraPreview: false,
        };
        this._unMounted = true;
    }

    /**
     * Generate the initial previews for audio input and video input.
     *
     * @inheritdoc
     */
    componentDidMount() {
        // !this.props._videoMuted ? $('#enableChk').prop('checked', true) : ''

        if (sessionStorage.getItem("roomDetails") !== null) {
            let roomDetails = JSON.parse(sessionStorage.getItem("roomDetails"));
            this.setState({
                room: roomDetails.room,
                profile_image: roomDetails.profile_image,
                contact: roomDetails.contact,
                headline_message: roomDetails.headline_message,
            });
        }

        const settings = localStorage.getItem("features/base/settings");
        const selector = document.querySelector("#_joinWithVideo");

        if (localStorage.joinWithVideoMuted === "N") {
            selector.checked = true;
            // if (!selector.checked) {
            //     selector.click()
            // }
        }
        // if (settings !== null && localStorage.joinWithVideoMuted === 'N') {
        //     if (JSON.parse(settings).hasOwnProperty('userSelectedCameraDeviceId')) {
        //         if (!selector.checked) {
        //             selector.click()
        //         }
        //     }
        // }

        this._unMounted = false;
        Promise.all([
            this._createAudioInputTrack(this.props.selectedAudioInputId),
            this._createVideoInputTrack(this.props.selectedVideoInputId),
        ])
            .catch((err) =>
                // logger.warn("Failed to initialize preview tracks", err)
                console.log('errr')
            )
            .then(() => this.props.mountCallback && this.props.mountCallback());
    }

    /**
     * Checks if audio / video permissions were granted. Updates audio input and
     * video input previews.
     *
     * @param {Object} prevProps - Previous props this component received.
     * @param {Object} prevState - Previous state this component had.
     * @returns {void}
     */
    componentDidUpdate(prevProps, prevState) {
        const { previewAudioTrack, previewVideoTrack } = prevState;

        if (
            (!previewAudioTrack && this.state.previewAudioTrack) ||
            (!previewVideoTrack && this.state.previewVideoTrack)
        ) {
            Promise.all([
                JitsiMeetJS.mediaDevices.isDevicePermissionGranted("audio"),
                JitsiMeetJS.mediaDevices.isDevicePermissionGranted("video"),
            ]).then((r) => {
                const [hasAudioPermission, hasVideoPermission] = r;

                this.setState({
                    hasAudioPermission,
                    hasVideoPermission,
                });
            });
        }

        if (
            prevProps.selectedAudioInputId !== this.props.selectedAudioInputId
        ) {
            this._createAudioInputTrack(this.props.selectedAudioInputId);
        }

        if (
            prevProps.selectedVideoInputId !== this.props.selectedVideoInputId
        ) {
            this._createVideoInputTrack(this.props.selectedVideoInputId);
        }
    }

    /**
     * Ensure preview tracks are destroyed to prevent continued use.
     *
     * @inheritdoc
     */
    componentWillUnmount() {
        this._unMounted = true;
        this._disposeAudioInputPreview();
        this._disposeVideoInputPreview();
    }

    joinWithVideo = (e) => {
        if (e.target.checked) {
            localStorage.setItem("joinWithVideoMuted", "N");
            this.setState({ cameraPreview: true });
        } else {
            localStorage.setItem("joinWithVideoMuted", "Y");
            this.setState({ cameraPreview: false });
        }
    };

    /**
     * Implements React's {@link Component#render()}.
     *
     * @inheritdoc
     */
    render() {
        const {
            hideAudioInputPreview,
            hideAudioOutputSelect,
            selectedAudioOutputId,
        } = this.props;

        let textToShow = !this.state.hasVideoPermission
            ? "video"
            : !this.state.hasAudioPermission
                ? "audio"
                : "audio/video";

        return (
            <>
                <div class="body-content page-content updatedWrap">
                    <div class="container-fluid p-0">
                        <div class="modal-header">
                            <h6
                                class="modal-title capcase mr-auto"
                                id="exampleModalLabel"
                            >
                                Audio / Video Setting
                            </h6>
                            <button
                                type="button"
                                class="close"
                                data-dismiss="modal"
                                onClick={() =>
                                    this.props.dispatch(hideDialog())
                                }
                                aria-label="Close"
                            >
                                <span aria-hidden="true">×</span>
                            </button>
                        </div>
                        <div class="modal-body">
                            <div class="updatedWraprow">
                                <div class="col-md-12 av-pop">
                                    <div
                                        id="step7-first-content"
                                        class="pl-0 pr-0"
                                    >
                                        <div className="row mt-3 device-selection">
                                            <div class="col-md-7 pr-3 border-R audioWrap">
                                                <div className="device-selection-column column-selectors">
                                                    <div className="device-selectors">
                                                        {this._renderSelectors()}
                                                    </div>
                                                </div>
                                                <div className="playsound">
                                                    <div className="row">
                                                        <div className="col-md-7">
                                                            <small className="micro-text">
                                                                Microphone Test
                                                            </small>
                                                            {!hideAudioInputPreview && (
                                                                <AudioInputPreview
                                                                    track={
                                                                        this
                                                                            .state
                                                                            .previewAudioTrack
                                                                    }
                                                                />
                                                            )}
                                                        </div>
                                                        <div className="col-md-5">
                                                            <small className="micro-text">
                                                                &nbsp;
                                                            </small>
                                                            {!hideAudioOutputSelect && (
                                                                <AudioOutputPreview
                                                                    deviceId={
                                                                        selectedAudioOutputId
                                                                    }
                                                                />
                                                            )}
                                                        </div>
                                                    </div>
                                                </div>

                                                {!this.state
                                                    .hasVideoPermission ||
                                                    !this.state
                                                        .hasAudioPermission ? (
                                                        <p className="locktext">
                                                            To enable {textToShow},
                                                        please click on{" "}
                                                            <i
                                                                class="fa fa-lock"
                                                                aria-hidden="true"
                                                            ></i>{" "}
                                                        icon in browser address
                                                        bar and allow{" "}
                                                            {textToShow} permission.
                                                        You will be asked to
                                                        reload once this is
                                                        done.
                                                        </p>
                                                    ) : (
                                                        ""
                                                    )}
                                            </div>
                                            <div class="col-md-5 pl-3 audioWrap">
                                                <div className="device-selectors">
                                                    {this._renderVideoSelectors()}
                                                </div>
                                                <div className="device-selection-column column-video">
                                                    <div
                                                        id="vidPreview1"
                                                        className={`device-selection-video-container`}
                                                    >
                                                        <VideoInputPreview
                                                            error={
                                                                this.state
                                                                    .previewVideoTrackError
                                                            }
                                                            track={
                                                                this.state
                                                                    .previewVideoTrack
                                                            }
                                                        />
                                                    </div>
                                                </div>
                                            </div>
                                            <div class="col-md-12 d-flex align-items-center justify-content-between mt-3">
                                                <button
                                                    type="button"
                                                    onClick={() =>
                                                        this.props.dispatch(
                                                            hideDialog()
                                                        )
                                                    }
                                                    class="btn btn-stroke"
                                                >
                                                    Close
                                                </button>
                                                <button
                                                    type="button"
                                                    onClick={
                                                        this.saveAvSettings
                                                    }
                                                    class="btn btn-default btn-default-sm"
                                                >
                                                    Save
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div class="modal-footer"></div>
                    </div>
                </div>
            </>
        );
    }

    saveAvSettings = () => {
        const mediaType = this.props._videoMediaType;
        const {
            selectedVideoInputId,
            _videoMuted,
            selectedAudioInputId,
            selectedAudioOutputId,
        } = this.props;

        if (localStorage.joinWithVideoMuted === "Y") {
            if (!_videoMuted) {
                sendAnalytics(createToolbarEvent(VIDEO_MUTE, { enable: true }));
                this.props.dispatch(
                    setVideoMuted(
                        true,
                        mediaType,
                        VIDEO_MUTISM_AUTHORITY.USER,
                        /* ensureTrack */ true
                    )
                );
            }
        } else {
            if (_videoMuted) {
                sendAnalytics(
                    createToolbarEvent(VIDEO_MUTE, { enable: false })
                );
                this.props.dispatch(
                    setVideoMuted(
                        false,
                        mediaType,
                        VIDEO_MUTISM_AUTHORITY.USER,
                        /* ensureTrack */ true
                    )
                );
            }

            if (localStorage.joinWithVideoMuted === "N") {
                sendAnalytics(createToolbarEvent(VIDEO_MUTE, { enable: true }));
                this.props.dispatch(
                    setVideoMuted(
                        true,
                        mediaType,
                        VIDEO_MUTISM_AUTHORITY.USER,
                        /* ensureTrack */ true
                    )
                );
            }
        }
        if (!this.state.switchCamra || selectedVideoInputId === "N") {
            this.props.dispatch(
                updateSettings({
                    userSelectedCameraDeviceId: selectedVideoInputId,
                    userSelectedCameraDeviceLabel: getDeviceLabelById(
                        APP.store.getState(),
                        selectedVideoInputId,
                        "videoInput"
                    ),
                })
            );

            this.props.dispatch(setVideoInputDevice(selectedVideoInputId));
        }
        this.props.dispatch(
            setAudioInputDeviceAndUpdateSettings(selectedAudioInputId)
        );
        this.props.dispatch(setAudioOutputDevice(selectedAudioOutputId));
        this.props.dispatch(hideDialog());
    };

    /**
     * Creates the JitiTrack for the audio input preview.
     *
     * @param {string} deviceId - The id of audio input device to preview.
     * @private
     * @returns {void}
     */
    _createAudioInputTrack(deviceId) {
        return this._disposeAudioInputPreview()
            .then(() => createLocalTrack("audio", deviceId))
            .then((jitsiLocalTrack) => {
                if (this._unMounted) {
                    jitsiLocalTrack.dispose();

                    return;
                }

                this.setState({
                    previewAudioTrack: jitsiLocalTrack,
                });
            })
            .catch(() => {
                this.setState({
                    previewAudioTrack: null,
                });
            });
    }

    /**
     * Creates the JitiTrack for the video input preview.
     *
     * @param {string} deviceId - The id of video device to preview.
     * @private
     * @returns {void}
     */
    _createVideoInputTrack(deviceId) {
        // this.setState({ switchCamra: true })
        return this._disposeVideoInputPreview()
            .then(() => createLocalTrack("video", deviceId))
            .then((jitsiLocalTrack) => {
                if (!jitsiLocalTrack) {
                    return Promise.reject();
                }

                if (this._unMounted) {
                    jitsiLocalTrack.dispose();

                    return;
                }

                this.setState({
                    previewVideoTrack: jitsiLocalTrack,
                    previewVideoTrackError: null,
                    // switchCamra: false
                });
            })
            .catch(() => {
                this.setState({
                    previewVideoTrack: null,
                    previewVideoTrackError: this.props.t(
                        "deviceSelection.previewUnavailable"
                    ),
                });
            });
    }

    /**
     * Utility function for disposing the current audio input preview.
     *
     * @private
     * @returns {Promise}
     */
    _disposeAudioInputPreview(): Promise<*> {
        return this.state.previewAudioTrack
            ? this.state.previewAudioTrack.dispose()
            : Promise.resolve();
    }

    /**
     * Utility function for disposing the current video input preview.
     *
     * @private
     * @returns {Promise}
     */
    _disposeVideoInputPreview(): Promise<*> {
        return this.state.previewVideoTrack
            ? this.state.previewVideoTrack.dispose()
            : Promise.resolve();
    }

    /**
     * Creates a DeviceSelector instance based on the passed in configuration.
     *
     * @private
     * @param {Object} deviceSelectorProps - The props for the DeviceSelector.
     * @returns {ReactElement}
     */
    _renderSelector(deviceSelectorProps) {
        const { connectionStatus } = this.props;
        return (
            <div className="form-group" key={deviceSelectorProps.label}>
                <div className="device-selector-label">
                    {this.props.t(deviceSelectorProps.label)}
                </div>
                {deviceSelectorProps.label === "settings.selectCamera" && (
                    <div class="form-group d-flex align-items-center av-switch">
                        <label class="switch mr-2">
                            <input
                                id="_joinWithVideo"
                                type="checkbox"
                                disabled={
                                    connectionStatus !== undefined &&
                                        connectionStatus[0] < 0.3
                                        ? true
                                        : false
                                }
                                onChange={(e) => this.joinWithVideo(e)}
                            />
                            <span class="slider round"></span>
                        </label>
                        Enable Camera/Video
                    </div>
                )}
                <DeviceSelector {...deviceSelectorProps} />
            </div>
        );
    }

    /**
     * Creates DeviceSelector instances for video output, audio input, and audio
     * output.
     *
     * @private
     * @returns {Array<ReactElement>} DeviceSelector instances.
     */
    _renderSelectors() {
        const { availableDevices } = this.props;
        const { hasAudioPermission, hasVideoPermission } = this.state;

        // if (availableDevices.videoInput.length > 0 && !availableDevices.videoInput.some((v, i, a) => v.deviceId === 'N')) {
        //     const camraDefaultOption = {
        //         deviceId: "N", kind: "videoinput", label: "No camera selected (Don't share video)",
        //         groupId: availableDevices.videoInput[0].groupId
        //     };

        //     availableDevices.videoInput.unshift(camraDefaultOption)
        // }

        const configurations = [
            {
                devices: availableDevices.audioInput,
                hasPermission: hasAudioPermission,
                icon: "icon-microphone",
                isDisabled:
                    this.props.disableAudioInputChange ||
                    this.props.disableDeviceChange,
                key: "audioInput",
                label: "settings.selectMic",
                onSelect: (selectedAudioInputId) =>
                    super._onChange({ selectedAudioInputId }),

                selectedDeviceId: this.state.previewAudioTrack
                    ? this.state.previewAudioTrack.getDeviceId()
                    : null,
            },
            !this.props.hideAudioOutputSelect
                ? {
                    devices: availableDevices.audioOutput,
                    hasPermission: hasAudioPermission || hasVideoPermission,
                    icon: "icon-volume-1",
                    isDisabled: this.props.disableDeviceChange,
                    key: "audioOutput",
                    label: "settings.selectAudioOutput",
                    onSelect: (selectedAudioOutputId) =>
                        super._onChange({ selectedAudioOutputId }),
                    selectedDeviceId:
                        this.props.selectedAudioOutputId === undefined
                            ? "default"
                            : this.props.selectedAudioOutputId,
                }
                : "",
            // {
            //     devices: availableDevices.videoInput.filter((v, i, a) => a.findIndex(t => (t.deviceId === v.deviceId)) === i),
            //     hasPermission: hasVideoPermission,
            //     icon: 'icon-camera',
            //     isDisabled: this.props.disableDeviceChange,
            //     key: 'videoInput',
            //     label: 'settings.selectCamera',
            //     onSelect: selectedVideoInputId => {

            //         this.setState({ switchCamra: false })
            //         localStorage.setItem('joinWithVideoMuted', 'N');
            //         document.querySelector('#_joinWithVideo').checked = true;
            //         super._onChange({ selectedVideoInputId })
            //     },
            //     // selectedDeviceId: this.state.previewVideoTrack
            //     //     ? this.props.selectedVideoInputId === 'N' || this.props.selectedVideoInputId === undefined || this.state.switchCamra ? 'N' : this.state.previewVideoTrack.getDeviceId() : null
            //     selectedDeviceId: this.state.previewVideoTrack
            //         ? this.state.previewVideoTrack.getDeviceId() : null
            // }
        ];

        // if (!this.props.hideAudioOutputSelect) {
        //     configurations.push({
        //         devices: availableDevices.audioOutput,
        //         hasPermission: hasAudioPermission || hasVideoPermission,
        //         icon: 'icon-speaker',
        //         isDisabled: this.props.disableDeviceChange,
        //         key: 'audioOutput',
        //         label: 'settings.selectAudioOutput',
        //         onSelect: selectedAudioOutputId =>
        //             super._onChange({ selectedAudioOutputId }),
        //         selectedDeviceId: this.props.selectedAudioOutputId
        //     });
        // }

        return configurations.map((config) => this._renderSelector(config));
    }

    _renderVideoSelectors() {
        const { availableDevices } = this.props;
        const { hasVideoPermission } = this.state;

        const configurations = [
            {
                devices: availableDevices.videoInput.filter(
                    (v, i, a) =>
                        a.findIndex((t) => t.deviceId === v.deviceId) === i
                ),
                hasPermission: hasVideoPermission,
                icon: "icon-camera",
                isDisabled: this.props.disableDeviceChange,
                key: "videoInput",
                label: "settings.selectCamera",
                onSelect: (selectedVideoInputId) => {
                    // if (selectedVideoInputId === 'N') {
                    //     localStorage.setItem('joinWithVideoMuted', 'Y');
                    // } else {
                    localStorage.setItem("joinWithVideoMuted", "N");
                    document.querySelector(
                        'input[type="checkbox"]'
                    ).checked = true;
                    // }
                    super._onChange({ selectedVideoInputId });
                },
                // selectedDeviceId: this.state.previewVideoTrack
                //     ? this.props.selectedVideoInputId === 'N' || this.props.selectedVideoInputId === undefined ? 'N' : this.state.previewVideoTrack.getDeviceId() : null
                selectedDeviceId: this.state.previewVideoTrack
                    ? this.state.previewVideoTrack.getDeviceId()
                    : null,
            },
        ];

        return configurations.map((config) => this._renderSelector(config));
    }
}

function _mapStateToProps(state) {
    const tracks = state["features/base/tracks"];
    return {
        _audioMuted: isLocalTrackMuted(tracks, MEDIA_TYPE.AUDIO),
        _videoMuted: isLocalTrackMuted(tracks, MEDIA_TYPE.VIDEO),
        _videoMediaType: getLocalVideoType(tracks),
    };
}

export default reactReduxConnect(_mapStateToProps)(
    translate(AvDeviceSelection)
);
